import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { type AppLocale } from '@irl/types';

import { useAuthStore } from '@/stores/auth';

import {
  detectLocale,
  getStoredLocale,
  isLocaleManual,
  persistLocale,
  readTelegramLanguageCode,
} from './detect-locale.js';
import { createTranslator, formatDateTime, formatDateTimeInTimezone } from './translate.js';

export interface I18nContextValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale, manual?: boolean) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  formatDateTime: (value: string | Date) => string;
  formatDateTimeInTimezone: (value: string | Date, timezone: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }): ReactElement {
  const userLanguageCode = useAuthStore((state) => state.user?.languageCode);
  const [locale, setLocaleState] = useState<AppLocale>(() =>
    detectLocale({
      stored: getStoredLocale(),
      telegramLanguageCode: readTelegramLanguageCode(),
      browserLanguage: navigator.language,
    }),
  );

  useEffect(() => {
    if (isLocaleManual()) return;
    const next = detectLocale({
      stored: getStoredLocale(),
      telegramLanguageCode: userLanguageCode ?? readTelegramLanguageCode(),
      browserLanguage: navigator.language,
    });
    setLocaleState(next);
  }, [userLanguageCode]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: AppLocale, manual = true) => {
    persistLocale(next, manual);
    setLocaleState(next);
  }, []);

  const value = useMemo<I18nContextValue>(() => {
    const t = createTranslator(locale);
    return {
      locale,
      setLocale,
      t,
      formatDateTime: (value: string | Date) => formatDateTime(value, locale),
      formatDateTimeInTimezone: (value: string | Date, timezone: string) =>
        formatDateTimeInTimezone(value, timezone, locale),
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
}
